import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  Stat,
  StatusPill,
} from "@/components/dash/ui";

export default function FailuresPage() {
  const q = useQuery({ queryKey: ["failures"], queryFn: dashApi.failures, refetchInterval: 30_000 });

  const order = ["critical", "warning", "info"];
  const alerts = (q.data?.alerts ?? []).slice().sort(
    (a, b) => order.indexOf(a.severity) - order.indexOf(b.severity),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Failures & Alerts"
        description="Rule-based warnings across connectors, operations, billing and sales."
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Critical" value={<span className="text-rose-300">{q.data?.counts.critical ?? 0}</span>} />
            <Stat label="Warning" value={<span className="text-amber-300">{q.data?.counts.warning ?? 0}</span>} />
            <Stat label="Info" value={<span className="text-sky-300">{q.data?.counts.info ?? 0}</span>} />
          </div>

          <Panel title={`Open alerts (${q.data?.total ?? 0})`}>
            {!alerts.length ? (
              <EmptyState>✓ No open alerts. All monitored conditions are within thresholds.</EmptyState>
            ) : (
              <ul className="divide-y divide-ink-500/40">
                {alerts.map((a) => (
                  <li key={a.id} className="flex items-start gap-4 py-3">
                    <StatusPill status={a.severity} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-100">{a.title}</p>
                        {a.source && <SourceBadge code={a.source} />}
                      </div>
                      {a.detail && <p className="mt-0.5 text-xs text-slate-400">{a.detail}</p>}
                      <p className="mt-1 text-[11px] text-slate-600">
                        rule <span className="font-mono">{a.rule}</span> · first seen {fmtRelative(a.first_seen_at)} · last {fmtRelative(a.last_seen_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
